/**
 * Change Password Form Component
 *
 * Allows users to update their password with current password verification.
 */

import React, { useState } from 'react';
import {
  Card,
  Title,
  Text,
  Stack,
  PasswordInput,
  Button,
  Alert,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { api } from '../api/client';

interface ChangePasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export const ChangePasswordForm: React.FC = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<ChangePasswordFormData>({
    initialValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    validate: {
      current_password: (value) =>
        value.length > 0 ? null : 'Current password is required',
      new_password: (value) =>
        value.length >= 8 ? null : 'Password must be at least 8 characters',
      confirm_password: (value, values) =>
        value === values.new_password ? null : 'Passwords do not match',
    },
  });

  const handleSubmit = async (values: ChangePasswordFormData) => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Send only current_password and new_password to backend
      await api.post('/api/auth/change-password', {
        current_password: values.current_password,
        new_password: values.new_password,
      });
      setSuccess(true);
      form.reset();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to change password';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder p="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <div>
            <Title order={4}>Change Password</Title>
            <Text size="sm" c="dimmed">
              Update your password to keep your account secure.
            </Text>
          </div>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}

          {success && (
            <Alert icon={<IconCheck size={16} />} color="green">
              Password changed successfully.
            </Alert>
          )}

          <PasswordInput
            label="Current Password"
            placeholder="Enter your current password"
            required
            {...form.getInputProps('current_password')}
          />

          <PasswordInput
            label="New Password"
            placeholder="Enter your new password"
            required
            description="Must be at least 8 characters"
            {...form.getInputProps('new_password')}
          />

          <PasswordInput
            label="Confirm New Password"
            placeholder="Re-enter your new password"
            required
            {...form.getInputProps('confirm_password')}
          />

          <Group justify="flex-end">
            <Button type="submit" loading={saving}>
              Change Password
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
};
